import { stockMatchIngredientName } from "./ingredientLine.js";
// Straight from the identity module rather than through mealAssembler's
// re-export of it. Same function either way, but the indirection made
// shopping -> mealAssembler an edge, and mealAssembler now needs to call back
// into resolveIngredientProductId (#1099) — which would have been a cycle.
import { normalizeFoodIdentity } from "./foodIdentity.js";
import { availableQuantity } from "./stock.js";
// Largest batch one "check off remaining" request may carry (#702). The schema
// enforces it server-side; the web client imports the same number so it chunks
// to exactly what the endpoint accepts instead of discovering the cap as a 400.
export const SHOPPING_TRIP_ITEMS_BULK_LIMIT = 100;
const sizeUnits = new Set([
    "oz",
    "ounce",
    "ounces",
    "fl oz",
    "fluid ounce",
    "fluid ounces",
    "lb",
    "lbs",
    "pound",
    "pounds",
    "g",
    "gram",
    "grams",
    "kg",
    "kilogram",
    "kilograms",
    "mg",
    "milligram",
    "milligrams",
    "ml",
    "milliliter",
    "milliliters",
    "l",
    "liter",
    "liters",
    "gal",
    "gallon",
    "gallons",
    "qt",
    "quart",
    "quarts",
    "pt",
    "pint",
    "pints",
    "cup",
    "cups",
    "tsp",
    "tbsp"
]);
const quantityUnits = new Set([
    "each",
    "ea",
    "ct",
    "count",
    "dozen",
    "pair",
    "pack",
    "packs",
    "case",
    "bag",
    "bags",
    "box",
    "boxes",
    "bottle",
    "bottles",
    "can",
    "cans",
    "jar",
    "jars",
    "carton",
    "cartons",
    "tube",
    "tubes",
    "roll",
    "rolls",
    "bunch",
    "loaf",
    "tray",
    "bar",
    "slice",
    "serving",
    "servings"
]);
function cleanNoteLine(value) {
    return value
        .replace(/^\s*(?:[-*]|\u2022)\s+\[[ xX]\]\s+/, "")
        .replace(/^\s*(?:[-*]|\u2022)\s+/, "")
        .replace(/^\s*\d+[.)]\s+/, "")
        .trim();
}
function cleanSegment(value) {
    return value.trim().replace(/[.;]+$/, "").trim();
}
function parseMoneyMinor(value) {
    const parsed = Number(value.replace(/[$,\s]/g, ""));
    return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) : null;
}
function parseMeasure(segment) {
    const match = segment.match(/^(\d+(?:\.\d+)?)\s+([a-z][a-z ]{0,24})$/i);
    if (!match)
        return null;
    const quantity = Number(match[1]);
    const unit = match[2]?.trim().toLowerCase() ?? "";
    if (!Number.isFinite(quantity) || quantity <= 0 || !unit)
        return null;
    return { quantity, unit };
}
function parseLeadingQuantity(value) {
    const compact = value.match(/^(\d+(?:\.\d+)?)\s*x\s+(.+)$/i);
    if (compact?.[1] && compact[2]) {
        return { quantity: Number(compact[1]), unit: "", name: compact[2].trim() };
    }
    const measured = value.match(/^(\d+(?:\.\d+)?)\s+([a-z][a-z]{0,24})\s+(.+)$/i);
    if (measured?.[1] && measured[2] && measured[3]) {
        const unit = measured[2].toLowerCase();
        if (quantityUnits.has(unit)) {
            return { quantity: Number(measured[1]), unit, name: measured[3].trim() };
        }
        if (sizeUnits.has(unit)) {
            return { quantity: null, unit: "", name: value };
        }
    }
    const countOnly = value.match(/^(\d+(?:\.\d+)?)\s+(.+)$/i);
    if (countOnly?.[1] && countOnly[2]) {
        const firstWord = countOnly[2].trim().split(/\s+/)[0]?.toLowerCase() ?? "";
        if (!sizeUnits.has(firstWord) && !quantityUnits.has(firstWord)) {
            return { quantity: Number(countOnly[1]), unit: "", name: countOnly[2].trim() };
        }
    }
    const trailing = value.match(/^(.+?)\s+x\s*(\d+(?:\.\d+)?)$/i);
    if (trailing?.[1] && trailing[2]) {
        return { quantity: Number(trailing[2]), unit: "", name: trailing[1].trim() };
    }
    return { quantity: null, unit: "", name: value };
}
export function parseShoppingNoteLine(line) {
    let working = cleanNoteLine(line);
    if (!working)
        return null;
    const urlMatch = working.match(/https?:\/\/\S+/i);
    const productUrl = urlMatch?.[0].replace(/[),.;]+$/, "") ?? "";
    if (urlMatch)
        working = `${working.slice(0, urlMatch.index)} ${working.slice((urlMatch.index ?? 0) + urlMatch[0].length)}`.trim();
    const priceMatch = working.match(/\$\s*\d[\d,]*(?:\.\d{1,2})?/);
    const expectedPriceMinor = priceMatch ? parseMoneyMinor(priceMatch[0]) : null;
    if (priceMatch)
        working = `${working.slice(0, priceMatch.index)} ${working.slice((priceMatch.index ?? 0) + priceMatch[0].length)}`.trim();
    const leading = parseLeadingQuantity(working);
    working = leading.name;
    const parts = working.split(",").map(cleanSegment).filter(Boolean);
    const name = cleanSegment(parts.shift() ?? working);
    if (!name)
        return null;
    let unit = leading.unit;
    let size = "";
    let parsedQuantity = leading.quantity;
    const notes = [];
    for (const part of parts) {
        const measure = parseMeasure(part);
        if (measure && sizeUnits.has(measure.unit) && !size) {
            size = part;
            continue;
        }
        if (measure && quantityUnits.has(measure.unit) && parsedQuantity == null) {
            parsedQuantity = measure.quantity;
            unit = measure.unit;
            continue;
        }
        if (/^qty[:\s]/i.test(part)) {
            const qtyMeasure = parseMeasure(part.replace(/^qty[:\s]*/i, ""));
            if (qtyMeasure && parsedQuantity == null) {
                parsedQuantity = qtyMeasure.quantity;
                unit = qtyMeasure.unit;
            }
            continue;
        }
        notes.push(part);
    }
    return {
        name,
        ...(parsedQuantity == null ? {} : { quantity: parsedQuantity }),
        ...(unit ? { unit } : {}),
        ...(size ? { size } : {}),
        ...(productUrl ? { productUrl } : {}),
        ...(expectedPriceMinor == null ? {} : { expectedPriceMinor, priceCurrency: "USD" }),
        ...(notes.length ? { notes: notes.join(", ") } : {})
    };
}
// Canonicalize the unit words a price-tag OCR pass tends to emit so callers get a
// stable unitSizeUnit ("lb", not "lbs"/"pound"/"pounds").
const UNIT_ALIASES = {
    lb: "lb", lbs: "lb", pound: "lb", pounds: "lb",
    oz: "oz", ounce: "oz", ounces: "oz",
    gal: "gal", gallon: "gal", gallons: "gal",
    kg: "kg", kilogram: "kg", kilograms: "kg",
    g: "g", gram: "g", grams: "g",
    ea: "ea", each: "ea"
};
const UNIT_WORD_PATTERN = "(lbs?|pounds?|ounces?|oz|gal(?:lons?)?|kg|kilograms?|grams?|g|each|ea)";
function normalizeUnitWord(word) {
    return UNIT_ALIASES[word.toLowerCase()] ?? word.toLowerCase();
}
function dollarsToMinor(raw) {
    const parsed = Number(raw.replace(/[$,\s]/g, ""));
    return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) : null;
}
function centsToMinor(raw) {
    const parsed = Number(raw.trim());
    return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : null;
}
/**
 * Parse a detected price off a snapped grocery price tag (see #604). Price tags are messy —
 * weighed-meat labels carry both a per-pound rate and a computed total, produce tags mix sale
 * and "regular" prices, multi-buy tags advertise "2/$9" alongside a per-unit rate — so this is
 * deliberately conservative: it returns null rather than a guess it isn't confident about, and
 * it does NOT attempt to model sale-vs-regular pricing (that's #49's job, not this one's).
 *
 * Priority order for the primary `priceMinor`:
 *   1. An explicit "TOTAL PRICE $X" (weighed-item labels compute this for you — best signal).
 *   2. The first plain package price ("$X.YZ" not immediately followed by "/unit").
 *   3. If neither exists, the first per-unit rate ("$X.YZ/lb" or "N¢/lb") stands in as the price
 *      (e.g. a per-lb-only produce tag has no other number to report).
 *
 * Amounts following "SAVE"/"REGULAR"/"WAS"/"REG" are discounts or reference prices, not the tag's
 * prominent price, and are excluded entirely before the above scan runs. "N/$M" multi-buy offers
 * and bare "$X/unit" per-unit rates are never mistaken for each other. Dates and PLU codes never
 * match because both require a leading "$" or trailing "¢" that dates/PLUs never carry.
 */
export function parsePriceFromOcr(ocrText) {
    let working = ocrText;
    // Blank out "SAVE $X" / "REGULAR $X/lb" / "WAS $X" / "REG $X" style amounts (dollars or
    // cents, optionally with a per-unit suffix) so they never become candidates below.
    working = working.replace(new RegExp(`\\b(?:save|regular|reg|was)\\b[^$¢\\n]{0,12}(?:\\$\\s*\\d+(?:\\.\\d{1,2})?(?:\\s*\\/\\s*${UNIT_WORD_PATTERN})?|\\d+(?:\\.\\d+)?\\s*¢(?:\\s*\\/\\s*${UNIT_WORD_PATTERN})?)`, "gi"), (match) => " ".repeat(match.length));
    // Blank out "N/$M" multi-buy offers ("2/$9") — a rate, not a per-item price.
    working = working.replace(/\d+\s*\/\s*\$\s*\d+(?:\.\d{1,2})?/g, (match) => " ".repeat(match.length));
    const candidates = [];
    // 1. Explicit total, if the tag prints one (weighed-item labels: price/lb * net weight).
    const totalMatch = working.match(/total\s+price\s*:?\s*\$\s*(\d+(?:\.\d{1,2})?)/i);
    const totalPriceMinor = totalMatch ? dollarsToMinor(totalMatch[1] ?? "") : null;
    if (totalPriceMinor != null)
        candidates.push(totalPriceMinor);
    // 2. Per-unit rate ("$6.29/lb" or "99¢/lb") — captured regardless of whether it ends up
    //    being the primary price, since it also feeds unitPriceMinor/unitSizeUnit below.
    const unitPricePattern = new RegExp(`\\$\\s*(\\d+(?:\\.\\d{1,2})?)\\s*\\/\\s*${UNIT_WORD_PATTERN}\\b`, "gi");
    const centsUnitPricePattern = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*¢\\s*\\/\\s*${UNIT_WORD_PATTERN}\\b`, "gi");
    const unitMatches = [];
    for (const m of working.matchAll(unitPricePattern)) {
        const minor = dollarsToMinor(m[1] ?? "");
        if (minor != null && m.index != null && m[2])
            unitMatches.push({ index: m.index, minor, unit: normalizeUnitWord(m[2]) });
    }
    for (const m of working.matchAll(centsUnitPricePattern)) {
        const minor = centsToMinor(m[1] ?? "");
        if (minor != null && m.index != null && m[2])
            unitMatches.push({ index: m.index, minor, unit: normalizeUnitWord(m[2]) });
    }
    unitMatches.sort((a, b) => a.index - b.index);
    for (const m of unitMatches)
        candidates.push(m.minor);
    // 3. Plain package price ("$24.53") not immediately followed by "/unit" (that's a unit
    //    rate, handled above) — this is what a non-weighed item's price tag usually shows.
    const plainPricePattern = /\$\s*(\d+(?:\.\d{1,2})?)(?![\d.])(?!\s*\/)/g;
    const plainMatches = [];
    for (const m of working.matchAll(plainPricePattern)) {
        const minor = dollarsToMinor(m[1] ?? "");
        if (minor != null && m.index != null)
            plainMatches.push({ index: m.index, minor });
    }
    plainMatches.sort((a, b) => a.index - b.index);
    for (const m of plainMatches)
        candidates.push(m.minor);
    // Decide the primary price using the priority order documented above.
    let priceMinor = null;
    if (totalPriceMinor != null) {
        priceMinor = totalPriceMinor;
    }
    else if (plainMatches.length > 0) {
        priceMinor = plainMatches[0]?.minor ?? null;
    }
    else if (unitMatches.length > 0) {
        priceMinor = unitMatches[0]?.minor ?? null;
    }
    if (priceMinor == null)
        return null;
    const firstUnit = unitMatches[0];
    // Net weight ("NET WT 3.90 lb" / "NET WEIGHT 3.90 lb") gives the package's actual size,
    // independent of whichever price pattern won above.
    const netWtMatch = working.match(new RegExp(`net\\s*w(?:t|eight)\\.?\\s*(\\d+(?:\\.\\d+)?)\\s*${UNIT_WORD_PATTERN}\\b`, "i"));
    const result = {
        priceMinor,
        currency: "USD",
        ...(firstUnit ? { unitPriceMinor: firstUnit.minor, unitSizeUnit: firstUnit.unit } : {}),
        ...(candidates.length ? { candidates } : {})
    };
    if (netWtMatch?.[1] && netWtMatch[2]) {
        const size = Number(netWtMatch[1]);
        if (Number.isFinite(size) && size > 0) {
            result.unitSize = size;
            result.unitSizeUnit = normalizeUnitWord(netWtMatch[2]);
        }
    }
    return result;
}
export function parseShoppingNoteLines(text) {
    return text
        .split(/\r?\n/)
        .map(parseShoppingNoteLine)
        .filter((item) => Boolean(item));
}
export function formatShoppingNoteLine(item) {
    return item.name.trim();
}
/**
 * True when a stock lot's unit can be counted against an ingredient's unit
 * without conversion: equal after normalization (lowercase, trimmed, naive
 * plural strip), or either side blank (unknowable — stay permissive, matching
 * how eggs-style countables are recorded). Real unit conversion is #587's
 * matcher work; unconvertible stock is simply not counted, which errs toward
 * buying — the safe direction (codex review).
 */
function unitCountsAgainst(ingredientUnit, lotUnit) {
    const normalize = (value) => {
        const trimmed = (value ?? "").trim().toLowerCase();
        return trimmed.length > 2 && trimmed.endsWith("s") ? trimmed.slice(0, -1) : trimmed;
    };
    const a = normalize(ingredientUnit);
    const b = normalize(lotUnit);
    if (!a || !b)
        return true;
    return a === b;
}
export function buildIngredientProductIndex(products, aliases = []) {
    const byIdentity = new Map();
    const ambiguous = new Set();
    const claim = (rawName, productId) => {
        if (!productId)
            return;
        const key = normalizeFoodIdentity(rawName);
        if (!key || ambiguous.has(key))
            return;
        const existing = byIdentity.get(key);
        if (existing === undefined) {
            byIdentity.set(key, productId);
            return;
        }
        if (String(existing) === String(productId))
            return;
        // Two DIFFERENT products claiming one identity is left unresolved rather
        // than arbitrarily assigned: products carry no name/identity uniqueness and
        // neither caller orders its select, so picking one would be
        // nondeterministic between the detail preview and the mutation. Crediting
        // one food's stock to another is the worst outcome here (#697), and
        // unresolved just means "buy it".
        ambiguous.add(key);
        byIdentity.delete(key);
    };
    for (const product of products)
        claim(product.name, product.id);
    // Snapshot the product-only keys BEFORE any alias claims one. A global alias
    // resolves its canonicalName against this frozen map, never against the map it
    // is still populating: the alias table permits arbitrary canonicalName values,
    // so "A -> B, B -> Product" would otherwise resolve only when B happened to be
    // selected first, and neither caller orders its alias query. Product names
    // only also matches what buildPantrySnapshot does, so the two agree.
    const canonicalProducts = new Map(byIdentity);
    // Aliases claim second so a canonical name always wins its own key; the
    // pantry snapshot behind the availability badge indexes these too, and
    // omitting them left the same ignored-stock divergence under another name.
    for (const alias of aliases) {
        const productId = alias.productId
            ?? (alias.canonicalName ? canonicalProducts.get(normalizeFoodIdentity(alias.canonicalName)) ?? null : null);
        claim(alias.name, productId);
    }
    return { byIdentity, ambiguous };
}
/**
 * The product an ingredient unambiguously means, or null.
 *
 * EXPORTED for #1048 PR-N3, so the recipe WRITE path can resolve links on save
 * using the same rule the read paths already use. It was module-private, and a
 * second implementation on the write side would be a second source of truth
 * about what a food IS — the exact defect epic #1034 exists to remove.
 */
export function resolveIngredientProductId(ingredient, productIndex) {
    if (ingredient.productId)
        return ingredient.productId;
    if (!productIndex)
        return null;
    const rawName = ingredient.name ?? "";
    // The EXACT name wins. A household can carry both "Garlic" and "Garlic,
    // minced" as separate foods, and stripping first would hand the minced
    // ingredient the generic garlic's stock.
    const rawKey = normalizeFoodIdentity(rawName);
    const exact = productIndex.byIdentity.get(rawKey);
    if (exact)
        return exact;
    // A contested exact name stops here rather than falling through: the looser
    // key would resolve it to whichever food happens to own that one.
    if (productIndex.ambiguous.has(rawKey))
        return null;
    // Only then the prep-stripped form — a recipe stores "garlic, minced" while
    // the household's food is "Garlic" (#931). Deliberately the STOCK-matching
    // strip, not the shopping-label one: the latter also drops "for ..." tails,
    // which would fold "cream, for whipping" onto a generic Cream and let that
    // stock suppress the whipping cream the recipe needs.
    const matchKey = normalizeFoodIdentity(stockMatchIngredientName(rawName));
    if (matchKey === rawKey || productIndex.ambiguous.has(matchKey))
        return null;
    return productIndex.byIdentity.get(matchKey) ?? null;
}
/**
 * On-hand quantity countable against one recipe ingredient, or `null` when the
 * ingredient cannot be stock-checked (no product link and no name match, or no
 * recipe quantity). Coverage is servings-independent, so a caller can compute it
 * once and then re-plan at any `servingsScale` without touching stock again —
 * that is what lets the UI preview and the add-recipe mutation agree (#696).
 */
export function ingredientStockCoverage(ingredient, stockLots, productIndex) {
    const productId = resolveIngredientProductId(ingredient, productIndex);
    if (!productId || ingredient.quantity == null)
        return null;
    const comparableLots = stockLots.filter((lot) => unitCountsAgainst(ingredient.unit, lot.unit));
    return availableQuantity(comparableLots, productId);
}
/**
 * Annotates every ingredient with its {@link ingredientStockCoverage}, plus the
 * product the coverage was read from. `resolvedProductId` is what lets a
 * name-matched ingredient that still needs buying reach the list as a linked
 * row instead of a loose string.
 */
export function recipeStockCoverage(ingredients, stockLots, productIndex) {
    return ingredients.map((ingredient) => ({
        ...ingredient,
        availableQuantity: ingredientStockCoverage(ingredient, stockLots, productIndex),
        resolvedProductId: resolveIngredientProductId(ingredient, productIndex)
    }));
}
/**
 * Splits a recipe's ingredients into what must be bought versus what on-hand
 * stock already covers, given coverage that was resolved earlier. An ingredient
 * with no quantity, or whose coverage could not be resolved at all, cannot be
 * checked against stock and always lands in `toBuy`.
 *
 * "Coverage could not be resolved" is the whole test — it used to also require a
 * product link, which meant a name-matched ingredient carried a real on-hand
 * figure and was told to buy anyway (#931).
 *
 * This is the single engine behind both the "Add N to <list>" preview the recipe
 * page shows and the `/shopping-lists/:id/add-recipe` mutation it triggers — they
 * used to be computed by two different matchers and disagreed routinely (#696).
 */
export function recipeShoppingPlanFromCoverage(ingredients, servingsScale = 1) {
    const toBuy = [];
    const alreadyHave = [];
    for (const ingredient of ingredients) {
        const required = ingredient.quantity == null ? null : Number((ingredient.quantity * servingsScale).toFixed(4));
        // The type makes coverage required, but this also runs on JSON that crossed
        // the wire, so an absent field still has to err toward buying rather than
        // toward a silent zero.
        const available = ingredient.availableQuantity ?? null;
        const productId = ingredient.resolvedProductId ?? ingredient.productId ?? null;
        if (required == null || available == null) {
            toBuy.push({
                productId,
                name: ingredient.name,
                quantity: required,
                unit: ingredient.unit ?? "",
                notes: ingredient.notes ?? ""
            });
            continue;
        }
        const missing = Number((required - available).toFixed(4));
        if (missing <= 0) {
            alreadyHave.push(ingredient.name);
            continue;
        }
        toBuy.push({
            productId,
            name: ingredient.name,
            quantity: missing,
            unit: ingredient.unit ?? "",
            notes: ingredient.notes ?? ""
        });
    }
    return { toBuy, alreadyHave };
}
/**
 * Splits a recipe's ingredients into what must be bought versus what on-hand
 * stock already covers. Convenience wrapper that resolves coverage from raw
 * stock lots and then runs {@link recipeShoppingPlanFromCoverage}.
 */
export function recipeShoppingPlan(ingredients, stockLots, servingsScale = 1, productIndex) {
    return recipeShoppingPlanFromCoverage(recipeStockCoverage(ingredients, stockLots, productIndex), servingsScale);
}
export function shoppingItemsForRecipe(ingredients, stockLots, servingsScale = 1) {
    return recipeShoppingPlan(ingredients, stockLots, servingsScale).toBuy;
}
/**
 * Which Thing templates look like they describe the same object as a food.
 *
 * SUGGEST-ONLY. Nothing here links anything; the server never writes a link a
 * person did not make. That is the whole discipline of this feature — a wrong
 * automatic link would quietly assert that two different objects are the same
 * thing, and nobody would go looking for it.
 *
 * Identity comes from the SAME machinery the pantry and shopping surfaces use
 * (`normalizeFoodIdentity` plus `buildIngredientProductIndex`'s claim/ambiguity
 * rules), so "Drill Bits" matching "drill bit" means here exactly what it means
 * there. Two consequences follow directly, and both are deliberate:
 *
 * - A CONTESTED identity is never suggested. If two products both claim
 *   "olive oil", the index refuses to pick one, and so does this — proposing a
 *   coin-flip is worse than proposing nothing, because the user would accept it.
 * - Aliases participate, so a household that calls its cordless drill "the
 *   drill" gets the suggestion its own vocabulary earns.
 *
 * Already-linked templates and already-claimed products are excluded: the link
 * is 1:1-optional, so a product another template already backs is not on offer.
 * Barcodes are deliberately absent — they are neither universal nor unique, and
 * identity lives at the definition record (owner decision, #1043).
 */
export function suggestDefinitionProductLinks(definitions, products, aliases = []) {
    const claimed = new Set(definitions.map((definition) => (definition.productId ? String(definition.productId) : null)).filter(Boolean));
    const index = buildIngredientProductIndex(products, aliases);
    const productNameById = new Map(products.map((product) => [String(product.id), product.name]));
    const suggestions = [];
    for (const definition of definitions) {
        if (definition.productId)
            continue;
        const key = normalizeFoodIdentity(definition.name ?? "");
        if (!key || index.ambiguous.has(key))
            continue;
        const productId = index.byIdentity.get(key);
        if (!productId || claimed.has(String(productId)))
            continue;
        suggestions.push({
            definitionId: definition.id,
            productId,
            productName: productNameById.get(String(productId)) ?? ""
        });
    }
    return suggestions;
}
